import { useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { finishIntro } from "../intro.js";
import "./IntroLoader.css";

const LOGO = "/assets/logo/version3_logo_b.webp";

const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Page-load intro: the Version3 lockup assembles on black (mark draws in, square
 * spins into place, wordmark wipes in), then the black curtain lifts up to the
 * hero frame's top edge so it hands over seamlessly to the hero's own intro.
 * The three logo pieces are the same image clipped, so they line up perfectly.
 */
export default function IntroLoader({ skip = false }) {
  const rootRef = useRef(null);
  const [gone, setGone] = useState(() => skip || reduceMotion());

  useLayoutEffect(() => {
    if (gone) {
      finishIntro();
      return;
    }
    const root = rootRef.current;
    const html = document.documentElement;
    html.classList.add("is-intro");

    const ctx = gsap.context(() => {
      const q = gsap.utils.selector(root);

      // where the curtain stops: the top of the hero frame (it's black too), else lift off entirely
      const curtainEnd = () => {
        const hf = document.querySelector(".hf");
        const top = hf ? hf.getBoundingClientRect().top : -1;
        return top >= 0 && top < window.innerHeight * 0.5
          ? `inset(${top}px 0px 0px 0px)`
          : "inset(0px 0px 100% 0px)";
      };

      gsap.set(root, { clipPath: "inset(0px 0px 0px 0px)" });
      gsap.set(q(".intro-mark"), { clipPath: "inset(0% 100% 100% 0%)" });
      gsap.set(q(".intro-square"), { scale: 0, rotation: -90, opacity: 0, transformOrigin: "33.4% 22%" });
      gsap.set(q(".intro-word"), { clipPath: "inset(50% 100% 0% 27.5%)", x: -16 });
      gsap.set(q(".intro-line span"), { scaleX: 0 });

      gsap
        .timeline({
          delay: 0.15,
          onComplete: () => {
            html.classList.remove("is-intro");
            finishIntro();
            setGone(true);
          },
        })
        .to(q(".intro-line span"), { scaleX: 1, duration: 2.1, ease: "power2.inOut" }, 0)
        .to(q(".intro-mark"), { clipPath: "inset(0% 73% 0% 0%)", duration: 1.1, ease: "expo.inOut" }, 0.05)
        .to(q(".intro-square"), { scale: 1, rotation: 0, opacity: 1, duration: 1, ease: "expo.out" }, 0.75)
        .to(q(".intro-word"), { clipPath: "inset(50% 0% 0% 27.5%)", x: 0, duration: 1.1, ease: "expo.out" }, 1.0)
        // hold, then out
        .to(q(".intro-lockup"), { y: -28, opacity: 0, duration: 0.6, ease: "power3.in" }, 2.5)
        .to(q(".intro-line"), { opacity: 0, duration: 0.4, ease: "power2.in" }, 2.5)
        .to(root, { clipPath: curtainEnd, duration: 1, ease: "expo.inOut" }, 2.95);
    }, root);

    return () => {
      ctx.revert();
      html.classList.remove("is-intro");
    };
  }, [gone]);

  if (gone) return null;

  return (
    <div className="intro" ref={rootRef} aria-hidden="true">
      <div className="intro-lockup">
        <img className="intro-mark" src={LOGO} alt="" fetchPriority="high" />
        <img className="intro-square" src={LOGO} alt="" />
        <img className="intro-word" src={LOGO} alt="" />
      </div>
      <div className="intro-line">
        <span />
      </div>
    </div>
  );
}
